import {
  fetchOrganizationBrandingUploadUrl,
  saveOrganizationSettings,
  type OrganizationSettingsData,
  type OrganizationSettingsSavePayload,
} from '../../api/accountsApi';
import { uploadFileWithProgress } from '../../utils/uploadWithProgress';
import type { FileEntry } from '../BrandIdentity/brandingShared';
import type { OnboardingFormData, OnboardingTrainingPackageValue } from './OnboardingWizard.types';
import { isStep2Valid } from './onboardingValidation';
// CEE-867: company website field removed
// import { normalizeWebsiteUrl } from '../../utils/websiteUrl';

type SubmitOnboardingWizardInput = {
  orgId: string;
  formData: OnboardingFormData;
  logoEntries: FileEntry[];
  documentEntries: FileEntry[];
  pendingInactiveFileIds: Set<string>;
};

async function uploadBrandingFiles(
  orgId: string,
  logoEntries: FileEntry[],
  documentEntries: FileEntry[],
): Promise<NonNullable<OrganizationSettingsSavePayload['uploadedFiles']>> {
  const uploadedFiles: NonNullable<OrganizationSettingsSavePayload['uploadedFiles']> = [];

  for (const entry of documentEntries) {
    const presign = await fetchOrganizationBrandingUploadUrl(orgId, {
      filename: entry.file.name,
      fileType: 'document',
    });
    const put = await uploadFileWithProgress(presign.uploadUrl, entry.file, () => {});
    if (!put.ok) {
      throw new Error('Upload to storage failed for a branding document.');
    }
    uploadedFiles.push({
      relativePath: presign.relativePath,
      fileType: 'document',
      originalFilename: entry.file.name,
      contentType: entry.file.type || undefined,
      fileSizeBytes: entry.file.size,
    });
  }

  for (const entry of logoEntries) {
    const presign = await fetchOrganizationBrandingUploadUrl(orgId, {
      filename: entry.file.name,
      fileType: 'logo',
    });
    const put = await uploadFileWithProgress(presign.uploadUrl, entry.file, () => {});
    if (!put.ok) {
      throw new Error('Upload to storage failed for a logo.');
    }
    const logoDimensions =
      entry.width != null && entry.height != null
        ? `${entry.width}X${entry.height}`
        : undefined;
    uploadedFiles.push({
      relativePath: presign.relativePath,
      fileType: 'logo',
      originalFilename: entry.file.name,
      contentType: entry.file.type || undefined,
      fileSizeBytes: entry.file.size,
      logoDimensions,
    });
  }

  return uploadedFiles;
}

function resolveTrainingPackageForSubmit(
  trainingPackage: string,
): OnboardingTrainingPackageValue | null {
  const normalized = trainingPackage.trim();
  return isStep2Valid(normalized) ? (normalized as OnboardingTrainingPackageValue) : null;
}

/** Maps wizard form state to POST /settings onboarding payload. */
export function buildOnboardingSettingsPayload(
  formData: OnboardingFormData,
  options?: {
    uploadedFiles?: NonNullable<OrganizationSettingsSavePayload['uploadedFiles']>;
    inactiveFileIds?: string[];
  },
): OrganizationSettingsSavePayload {
  // Opt-in only — never infer AI from a training package (CEE-992).
  const aiEnabled = formData.aiRecommendationEnabled === true;
  const trainingPackage = aiEnabled
    ? resolveTrainingPackageForSubmit(formData.trainingPackage)
    : null;

  if (aiEnabled && !trainingPackage) {
    throw new Error('Training package is required');
  }

  const brandGuidelines =
    !formData.brandSkipped && formData.brandGuidelines === true;
  const uploadedFiles = options?.uploadedFiles ?? [];

  return {
    companyName: formData.companyName.trim(),
    aiRecommendationEnabled: aiEnabled,
    industry: formData.industry.trim(),
    employeeCount: formData.employeeCount.trim(),
    employeeTerminology: formData.employeeTerminology.trim(),
    speakUpSolution: formData.speakUpSolution.trim(),
    regions: formData.regions,
    brandSkipped: formData.brandSkipped,
    trainingTopics: formData.trainingTopics,
    priorityMonths: formData.priorityMonths,
    monthsToAvoid: formData.monthsToAvoid,
    ...(aiEnabled && trainingPackage
      ? {
          selectedPackage: trainingPackage,
          trainingPackage,
        }
      : {}),
    ...(formData.brandSkipped
      ? { brandGuidelines: false }
      : { brandGuidelines }),
    ...(uploadedFiles.length > 0 ? { uploadedFiles } : {}),
    ...(options?.inactiveFileIds && options.inactiveFileIds.length > 0
      ? { inactiveFileIds: options.inactiveFileIds }
      : {}),
    ...(!formData.brandSkipped &&
    formData.brandGuidelines === false &&
    formData.primaryBrandColor.trim()
      ? { primaryBrandColor: formData.primaryBrandColor.trim().toUpperCase() }
      : {}),
    ...(!formData.brandSkipped &&
    formData.brandGuidelines === false &&
    formData.secondaryBrandColor.trim()
      ? { secondaryBrandColor: formData.secondaryBrandColor.trim().toUpperCase() }
      : {}),
  };
}

/** POST /settings with onboarding fields + optional brand uploads (wizard final submit). */
export async function submitOnboardingWizard({
  orgId,
  formData,
  logoEntries,
  documentEntries,
  pendingInactiveFileIds,
}: SubmitOnboardingWizardInput): Promise<OrganizationSettingsData> {
  const uploadedFiles =
    !formData.brandSkipped && (logoEntries.length > 0 || documentEntries.length > 0)
      ? await uploadBrandingFiles(orgId, logoEntries, documentEntries)
      : [];

  const payload = buildOnboardingSettingsPayload(formData, {
    uploadedFiles,
    ...(pendingInactiveFileIds.size > 0
      ? { inactiveFileIds: [...pendingInactiveFileIds] }
      : {}),
  });

  return saveOrganizationSettings(orgId, payload);
}
